import * as Sentry from "@sentry/react";
import { scrubDottieEvent, type SentryEventLike } from "@simplicity/agents/scrubber";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

// Runtime release identifier injected by the platform server when serving index.html
// (see apps/platform/src/sentryRelease.ts). Build-time injection doesn't work on
// Railway because RAILWAY_GIT_COMMIT_SHA is runtime-only; the platform stamps the
// release in as it serves the SPA HTML. Uses a meta tag to comply with CSP
// `script-src 'self'` (no inline scripts).

/**
 * Initialize Sentry for portal. Early-returns when no DSN is configured
 * (e.g., local dev) so Sentry stays silent unless explicitly enabled.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_PORTAL_SENTRY_DSN;
  if (!dsn) return;

  const injected =
    typeof document !== "undefined"
      ? document.querySelector<HTMLMetaElement>('meta[name="sentry-release"]')?.content
      : undefined;
  const release = typeof injected === "string" && injected ? injected : undefined;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_PORTAL_SENTRY_ENV ?? import.meta.env.MODE,
    release,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    // 100% sampling: today's portal volume is essentially zero. Weekly review is tracked
    // in SIM-420 — drop the rate as real client traffic ramps up (tuning ladder in the issue).
    tracesSampleRate: 1.0,
    // v11 removed `sendDefaultPii`; every `dataCollection` category defaults to
    // ON, so each is declared off explicitly. See history: SIM-2470, SIM-3355.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      mcp: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      stackFrameVariables: false,
    },
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],
    denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//],
    beforeSend(event, hint) {
      // Drop session-expiry fetch errors — 401/403 redirect to login, not a real error.
      const err = hint.originalException;
      if (err instanceof Error && /HTTP 40[13]/.test(err.message)) return null;
      // SIM-1404: registry-driven scrub of Dottie PII fields applied to ALL portal events as
      // defense-in-depth — the portal client has no separate Dottie DSN, so this keeps chat content
      // out of the portal Sentry project too. SIM-1520: the double cast through `unknown` is
      // load-bearing. `SentryEventLike` is a structural subset of `ErrorEvent` that diverges on
      // nested types, and the autofix below removed the cast once, which TS rightly rejected.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- SIM-1520: cast is load-bearing (see preceding comment); rule rejects but typecheck requires.
      return scrubDottieEvent(event as unknown as SentryEventLike) as unknown as typeof event;
    },
  });
}
